import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Download, Upload, Send, Loader2, FileText, CreditCard, Clock,
  CheckCircle, AlertCircle, XCircle, ArrowLeft, Paperclip, Lock, Banknote,
  Brain, Zap, Tag,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = {
  pending_review: { label: 'Pendiente de revisión', color: 'bg-yellow-500', icon: Clock },
  needs_info: { label: 'Falta información', color: 'bg-orange-500', icon: AlertCircle },
  accepted: { label: 'Aceptada / En cola', color: 'bg-blue-500', icon: CheckCircle },
  in_progress: { label: 'En proceso', color: 'bg-purple-500', icon: Loader2 },
  ready_to_pay: { label: 'Listo para pagar', color: 'bg-emerald-500', icon: Banknote },
  delivered: { label: 'Entregada', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Cerrada', color: 'bg-zinc-500', icon: CheckCircle },
  cancelled: { label: 'Cancelada', color: 'bg-red-500', icon: XCircle },
};
const STATE_ORDER = ['pending_review', 'needs_info', 'accepted', 'in_progress', 'ready_to_pay', 'delivered', 'closed'];

const TicketDetailPage = () => {
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [paying, setPaying] = useState(false);
  const [creditsToApply, setCreditsToApply] = useState(0);
  const [autoSolution, setAutoSolution] = useState(null);
  const [loadingAutoSolution, setLoadingAutoSolution] = useState(false);
  const [buyingVersion, setBuyingVersion] = useState(null);
  const chatRef = useRef(null);

  useEffect(() => {
    fetchTicket();
    if (searchParams.get('payment') === 'success') {
      toast.success('¡Pago completado! Ya puedes descargar tu archivo.');
      refreshUser();
    }
  }, [ticketId]);

  // Auto-refresh cada 10s para ver cambios del admin en tiempo real
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setTicket(prev => {
            if (prev && prev.status !== res.data.status) {
              toast.info(`Estado actualizado: ${STATES[res.data.status]?.label || res.data.status}`);
            }
            return res.data;
          });
        }).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [ticketId, token]);

  const fetchTicket = async () => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTicket(res.data);
      // If ticket has auto_match, load solution details
      if (res.data.auto_match?.solution_id && !autoSolution) {
        loadAutoSolution(res.data.auto_match.solution_id);
      }
    } catch { toast.error('Error al cargar solicitud'); }
    finally { setLoading(false); }
  };

  const loadAutoSolution = async (solutionId) => {
    setLoadingAutoSolution(true);
    try {
      const res = await axios.get(`${API}/solutions/${solutionId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res?.data) {
        setAutoSolution(res.data);
      }
    } catch {}
    setLoadingAutoSolution(false);
  };

  const handleQuickBuy = async (solutionId, versionId) => {
    setBuyingVersion(versionId);
    try {
      const payload = { solution_id: solutionId, version_id: versionId };
      if (ticket.reading_type === 'slave' && ticket.slave_machine_sn) {
        payload.machine_sn = ticket.slave_machine_sn;
      }
      const res = await axios.post(`${API}/quick-buy`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message || 'Compra completada!');
      await refreshUser();
      // Download file
      if (res.data.download_token) {
        try {
          const dl = await axios.get(`${API}/downloads/${res.data.download_token}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          });
          const url = window.URL.createObjectURL(dl.data);
          const a = document.createElement('a'); a.href = url; a.download = res.data.filename || 'modified.bin'; a.click();
          window.URL.revokeObjectURL(url);
        } catch {}
      }
      await fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la compra');
    }
    setBuyingVersion(null);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() && !attachment) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('message', message);
      if (attachment) fd.append('attachment', attachment);
      await axios.post(`${API}/tickets/${ticketId}/messages`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setMessage(''); setAttachment(null);
      await fetchTicket();
      setTimeout(() => chatRef.current?.scrollTo(0, chatRef.current.scrollHeight), 100);
    } catch { toast.error('Error al enviar'); }
    finally { setSending(false); }
  };

  const handleDownload = async (fileVersion) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}/download/${fileVersion.id}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
        timeout: 120000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = fileVersion.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 402) toast.error('Debes pagar para descargar el archivo modificado');
      else if (err.response?.status === 400) {
        // Try to read error from blob response
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          toast.error(json.detail || 'Error al descargar');
        } catch { toast.error('Archivo no disponible. Contacta al admin.'); }
      }
      else if (err.response?.status === 404) toast.error('Archivo no encontrado en el servidor');
      else toast.error(`Error al descargar (${err.response?.status || 'red'})`);
    }
  };

  const handlePay = async () => {
    setPaying(true);
    try {
      const fd = new FormData();
      fd.append('credits_to_apply', creditsToApply);
      fd.append('origin_url', window.location.origin);
      const res = await axios.post(`${API}/tickets/${ticketId}/pay`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.paid) {
        toast.success(`Pagado con ${res.data.credits_used} créditos`);
        await refreshUser();
        await fetchTicket();
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al pagar');
    } finally { setPaying(false); }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></Layout>;
  if (!ticket) return <Layout><p className="text-zinc-400 text-center py-20">Solicitud no encontrada</p></Layout>;

  const price = ticket.price || ticket.service_base_price || 0;
  const isPaid = ticket.payment_status === 'paid';
  const hasModified = ticket.file_versions?.some(v => v.type === 'modified');
  const needsPayment = !isPaid && hasModified && price > 0;
  const originals = ticket.file_versions?.filter(v => v.type === 'original' || v.type === 'original_decrypted') || [];
  const encrypted = ticket.file_versions?.filter(v => v.type === 'modified_encrypted') || [];
  const plainMods = ticket.file_versions?.filter(v => v.type === 'modified') || [];
  // For SLAVE tickets: show encrypted versions if available, otherwise show plain
  const modifieds = ticket.reading_type === 'slave' && encrypted.length > 0 ? encrypted : plainMods;
  const stateInfo = STATES[ticket.status] || STATES.pending_review;
  const maxCredits = Math.min(user?.credits || 0, Math.floor(price));
  const eurosRemaining = Math.max(0, price - creditsToApply);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="ticket-detail-page">
        <div className="flex items-start justify-between mb-6">
          <div>
            <a href="/tickets" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1 mb-2"><ArrowLeft className="w-3 h-3" />Volver</a>
            <h1 className="font-heading text-2xl font-bold text-white">{ticket.ticket_number}</h1>
            <p className="text-zinc-400 mt-1">{ticket.vehicle_brand} {ticket.vehicle_model} ({ticket.vehicle_year}) - {ticket.service_name}</p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-white ${stateInfo.color}`} data-testid="ticket-status">
            <stateInfo.icon className="w-4 h-4" />{stateInfo.label}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-surface border border-white/10 rounded-md p-4 mb-6" data-testid="ticket-timeline">
          <div className="flex items-center gap-1 overflow-x-auto">
            {STATE_ORDER.map((s, i) => {
              const current = STATE_ORDER.indexOf(ticket.status);
              const done = i <= current;
              const active = s === ticket.status;
              return (
                <React.Fragment key={s}>
                  <div className={`flex-shrink-0 w-3 h-3 rounded-full ${active ? STATES[s].color : done ? 'bg-green-500' : 'bg-zinc-700'}`} />
                  <span className={`text-xs whitespace-nowrap ${active ? 'text-white font-medium' : done ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    {STATES[s].label}
                  </span>
                  {i < STATE_ORDER.length - 1 && <div className={`flex-1 h-px min-w-4 ${done ? 'bg-green-500/50' : 'bg-zinc-700'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            {/* Auto-Match Solution Found by AI */}
            {ticket.auto_match && ticket.status === 'pending_review' && !isPaid && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-5" data-testid="auto-match-panel">
                <div className="flex items-center gap-3 mb-3">
                  <Brain className="w-6 h-6 text-blue-400" />
                  <div>
                    <h3 className="font-heading text-white font-bold">SOLUCION ENCONTRADA POR IA</h3>
                    <p className="text-blue-400/70 text-xs">
                      Match: {ticket.auto_match.match_type} ({Math.round(ticket.auto_match.confidence * 100)}% confianza)
                    </p>
                  </div>
                </div>
                <div className="bg-surface rounded-md p-4 mb-4">
                  <p className="text-white font-medium">{ticket.auto_match.vehicle} <span className="text-zinc-400">- {ticket.auto_match.ecu_type}</span></p>
                  <p className="text-zinc-500 text-xs mt-1">{ticket.auto_match.versions_count} version(es) disponible(s)</p>
                </div>

                {loadingAutoSolution ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-blue-400" /></div>
                ) : autoSolution?.modified_versions?.length > 0 ? (
                  <div className="space-y-2">
                    {autoSolution.modified_versions.map((ver, idx) => (
                      <div key={ver.id} className="flex items-center justify-between bg-surface border border-white/10 rounded-md p-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(ver.solution_names || ver.solutions || []).map((name, j) => (
                              <span key={j} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Tag className="w-2.5 h-2.5" />{name}
                              </span>
                            ))}
                          </div>
                          {ver.notes && <p className="text-zinc-500 text-xs">{ver.notes}</p>}
                        </div>
                        <div className="flex items-center gap-3 ml-3">
                          <span className="text-primary font-mono font-bold">{ver.price || ver.credits_cost || 60}cr</span>
                          <Button
                            size="sm"
                            onClick={() => handleQuickBuy(autoSolution.id || ticket.auto_match.solution_id, ver.id)}
                            disabled={buyingVersion === ver.id || (user?.credits || 0) < (ver.price || 60)}
                            className="bg-primary hover:bg-primary/90 text-white"
                            data-testid={`auto-buy-${idx}`}
                          >
                            {buyingVersion === ver.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <><Zap className="w-3 h-3 mr-1" />Compra Rapida</>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(user?.credits || 0) < 30 && (
                      <p className="text-amber-400 text-xs text-center mt-2">
                        Creditos insuficientes. Saldo: {user?.credits || 0} — <a href="/credits" className="underline">Comprar creditos</a>
                      </p>
                    )}
                  </div>
                ) : !loadingAutoSolution && (
                  <p className="text-zinc-400 text-sm">Solución detectada. El admin revisara tu solicitud con prioridad.</p>
                )}

                {ticket.file_analysis && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-3 text-xs">
                    {ticket.file_analysis.hw_id && <span className="text-zinc-500">HW: <span className="text-zinc-300 font-mono">{ticket.file_analysis.hw_id}</span></span>}
                    {ticket.file_analysis.sw_id && <span className="text-zinc-500">SW: <span className="text-zinc-300 font-mono">{ticket.file_analysis.sw_id}</span></span>}
                    {ticket.file_analysis.ecu_family && <span className="text-zinc-500">ECU: <span className="text-zinc-300 font-mono">{ticket.file_analysis.ecu_family}</span></span>}
                  </div>
                )}
              </div>
            )}

            {/* Payment Section */}
            {needsPayment && price > 0 && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-md p-5" data-testid="payment-section">
                <div className="flex items-center gap-3 mb-4">
                  <Banknote className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-heading text-white font-bold">PAGAR PARA DESCARGAR</h3>
                </div>
                <p className="text-zinc-400 text-sm mb-4">Tu archivo modificado está listo. Paga para desbloquearlo.</p>
                <p className="font-mono text-3xl text-primary font-bold mb-4">{price}€</p>

                {maxCredits > 0 && (
                  <div className="mb-5 bg-surface rounded-md p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-300 text-sm">Aplicar créditos</span>
                      <span className="font-mono text-primary">{creditsToApply} / {maxCredits}</span>
                    </div>
                    <Slider
                      value={[creditsToApply]}
                      onValueChange={(v) => setCreditsToApply(v[0])}
                      max={maxCredits}
                      step={1}
                      className="my-3"
                    />
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-500">Créditos: {creditsToApply}</span>
                      <span className="text-zinc-500">Restante: <span className="text-white font-mono font-bold">{eurosRemaining.toFixed(2)}€</span></span>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 flex-wrap">
                  <Button onClick={handlePay} disabled={paying} className="bg-primary hover:bg-primary/90 flex-1" data-testid="pay-btn">
                    {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : eurosRemaining > 0 ? <CreditCard className="w-4 h-4 mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    {eurosRemaining > 0
                      ? creditsToApply > 0
                        ? `Pagar ${creditsToApply} créditos + ${eurosRemaining.toFixed(2)}€`
                        : `Pagar ${eurosRemaining.toFixed(2)}€ con tarjeta`
                      : `Pagar con ${creditsToApply} créditos`
                    }
                  </Button>
                </div>
              </div>
            )}

            {/* Paid success */}
            {isPaid && hasModified && (
              <div className="bg-green-500/5 border border-green-500/20 rounded-md p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-green-400 font-medium">Pago completado</p>
                  <p className="text-zinc-500 text-sm">Ya puedes descargar tu archivo modificado</p>
                </div>
              </div>
            )}

            {/* Chat */}
            <div className="bg-surface border border-white/10 rounded-md" data-testid="chat-section">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-heading text-white font-bold">MENSAJES</h3>
              </div>
              <div ref={chatRef} className="p-4 space-y-4 max-h-96 overflow-y-auto">
                {(!ticket.messages || ticket.messages.length === 0) ? (
                  <p className="text-zinc-600 text-sm text-center py-4">Sin mensajes aún</p>
                ) : ticket.messages.map(m => (
                  <div key={m.id} className={`flex ${m.is_admin ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 ${m.is_admin ? 'bg-zinc-800' : 'bg-primary/10'}`}>
                      <p className={`text-xs mb-1 ${m.is_admin ? 'text-blue-400' : 'text-primary'}`}>{m.sender_name}{m.is_admin && ' (Admin)'}</p>
                      <p className="text-white text-sm whitespace-pre-wrap">{m.message}</p>
                      {m.attachment && (
                        <button onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/tickets/${ticketId}/attachment/${m.attachment.file_id}`, {
                              headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 120000
                            });
                            const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = m.attachment.filename; a.click(); URL.revokeObjectURL(url);
                          } catch { toast.error('Error al descargar adjunto'); }
                        }}
                          className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline">
                          <Paperclip className="w-3 h-3" />{m.attachment.filename}
                        </button>
                      )}
                      <p className="text-zinc-600 text-[10px] mt-1">{new Date(m.created_at).toLocaleString('es-ES')}</p>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Escribe un mensaje..." rows={1}
                    className="bg-surface-highlight border-white/10 text-white flex-1 min-h-[40px] max-h-24" data-testid="chat-input" />
                  <input type="file" className="hidden" id="chat-attach" onChange={e => setAttachment(e.target.files?.[0])} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => document.getElementById('chat-attach').click()}
                    className="text-zinc-500 hover:text-white"><Paperclip className="w-4 h-4" /></Button>
                  <Button type="submit" disabled={sending || (!message.trim() && !attachment)} className="bg-primary hover:bg-primary/90" data-testid="send-msg-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {attachment && <p className="text-xs text-primary mt-1">Adjunto: {attachment.name}</p>}
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-surface border border-white/10 rounded-md p-4 space-y-3" data-testid="ticket-info">
              <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider">Información</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Servicio</span><span className="text-white">{ticket.service_name}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Precio</span><span className="text-primary font-mono font-bold">{price > 0 ? `${price}€` : 'Pendiente'}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Pago</span><span className={isPaid ? 'text-green-400' : 'text-yellow-400'}>{isPaid ? 'Pagado' : 'Pendiente'}</span></div>
                {ticket.credits_applied > 0 && <div className="flex justify-between"><span className="text-zinc-400">Créditos usados</span><span className="text-primary">{ticket.credits_applied}</span></div>}
                <div className="flex justify-between"><span className="text-zinc-400">Unidad</span><span className="text-white">{ticket.unit_type?.toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Herramienta</span><span className="text-white">{ticket.reading_tool}</span></div>
                {ticket.reading_type && (
                  <div className="flex justify-between"><span className="text-zinc-400">Tipo lectura</span><span className={ticket.reading_type === 'slave' ? 'text-orange-400 font-medium' : 'text-green-400'}>{ticket.reading_type === 'slave' ? 'SLAVE' : 'MASTER'}</span></div>
                )}
                <div className="flex justify-between"><span className="text-zinc-400">ECU</span><span className="text-white">{ticket.ecu_type}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Matrícula</span><span className="text-white font-mono">{ticket.license_plate}</span></div>
                {ticket.vin && <div className="flex justify-between"><span className="text-zinc-400">VIN</span><span className="text-white font-mono text-xs">{ticket.vin}</span></div>}
                {ticket.transmission && <div className="flex justify-between"><span className="text-zinc-400">Transmisión</span><span className="text-white">{ticket.transmission === 'manual' ? 'Manual' : 'Automático'}</span></div>}
                {ticket.vehicle_type && <div className="flex justify-between"><span className="text-zinc-400">Tipo</span><span className={ticket.vehicle_type === 'industrial' ? 'text-orange-400 font-medium' : 'text-white'}>{ticket.vehicle_type === 'industrial' ? 'Industrial' : 'Turismo'}</span></div>}
                {ticket.estimated_time && <div className="flex justify-between"><span className="text-zinc-400">Estimado</span><span className="text-white">{ticket.estimated_time}</span></div>}
                <div className="flex justify-between"><span className="text-zinc-400">Fecha</span><span className="text-white">{new Date(ticket.created_at).toLocaleDateString('es-ES')}</span></div>
              </div>
              {ticket.notes && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-zinc-500 text-xs mb-1">Notas:</p>
                  <p className="text-zinc-300 text-sm">{ticket.notes}</p>
                </div>
              )}
            </div>

            {/* Files */}
            <div className="bg-surface border border-white/10 rounded-md p-4" data-testid="ticket-files">
              <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Archivos</h3>
              <div className="space-y-2">
                {originals.map(f => (
                  <button key={f.id} onClick={() => handleDownload(f)}
                    className="w-full flex items-center gap-2 p-2 rounded bg-surface-highlight hover:bg-white/10 transition-colors text-left">
                    <Upload className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-white text-xs truncate">{f.filename}</p><p className="text-zinc-600 text-[10px]">Original</p></div>
                    <Download className="w-3 h-3 text-zinc-500" />
                  </button>
                ))}
                {modifieds.map(f => (
                  <button key={f.id} onClick={() => handleDownload(f)}
                    className={`w-full flex items-center gap-2 p-2 rounded transition-colors text-left ${isPaid ? 'bg-green-500/5 hover:bg-green-500/10 border border-green-500/20' : 'bg-surface-highlight opacity-60'}`}>
                    <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs truncate">{f.filename}</p>
                      <p className="text-zinc-600 text-[10px]">
                        Modificado v{f.version}{f.notes && ` - ${f.notes}`}
                        {f.type === 'modified_encrypted' && <span className="text-orange-400 ml-1">(encriptado para SLAVE)</span>}
                        {f.type === 'modified' && ticket.reading_type === 'slave' && <span className="text-yellow-400 ml-1">(sin encriptar)</span>}
                      </p>
                    </div>
                    {isPaid ? <Download className="w-3 h-3 text-green-400" /> : <Lock className="w-3 h-3 text-zinc-500" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Audit Log */}
            {ticket.audit_logs?.length > 0 && (
              <div className="bg-surface border border-white/10 rounded-md p-4" data-testid="audit-log">
                <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">Historial</h3>
                <div className="space-y-2">
                  {ticket.audit_logs.slice(0, 10).map((log, i) => (
                    <div key={i} className="text-xs flex items-start gap-2">
                      {log.is_admin && (
                        <span className="bg-primary/20 text-primary px-1.5 py-0.5 rounded text-[9px] font-bold mt-0.5 flex-shrink-0">{log.user_name || 'Admin'}</span>
                      )}
                      <div className="flex-1">
                        <p className="text-zinc-300">{log.details || log.action}</p>
                        <p className="text-zinc-600">{new Date(log.created_at).toLocaleString('es-ES')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TicketDetailPage;
